import { InternalNode, Node, Position, XYPosition } from "@xyflow/react"

// Most of this code is from xyflow/examples/react/src/examples/FloatingEdges/utils.ts
// https://github.com/xyflow/xyflow/blob/main/examples/react/src/examples/FloatingEdges/utils.ts
// Slightly adjusted to match our requirements

// returns intersection point of line (center->target) with node border
// padding moves point OUTWARD from node only (used for arrow side)
function getNodeIntersection(
  intersectionNode: InternalNode,
  targetNode: InternalNode,
  padding = 0,
): XYPosition {
  const { internals: intersectionInternals } = intersectionNode
  const { width: intersectionNodeWidth = 0, height: intersectionNodeHeight = 0 } =
    intersectionNode.measured ?? { width: 0, height: 0 }
  const targetPosition = targetNode.internals.positionAbsolute

  const w = intersectionNodeWidth / 2 // no extra padding here
  const h = intersectionNodeHeight / 2

  const cx = intersectionInternals.positionAbsolute.x + w
  const cy = intersectionInternals.positionAbsolute.y + h
  const tx = targetPosition.x + w
  const ty = targetPosition.y + h

  // original diamond/rectangle intersection math (xyflow example)
  const xx1 = (tx - cx) / (2 * w) - (ty - cy) / (2 * h)
  const yy1 = (tx - cx) / (2 * w) + (ty - cy) / (2 * h)
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1))
  const xx3 = a * xx1
  const yy3 = a * yy1
  let x = w * (xx3 + yy3) + cx
  let y = h * (-xx3 + yy3) + cy

  if (padding > 0) {
    const vx = x - cx
    const vy = y - cy
    const len = Math.hypot(vx, vy) || 1
    x += (vx / len) * padding
    y += (vy / len) * padding
  }

  return { x, y }
}

// returns the position (top,right,bottom or right) passed node compared to the intersection point
function getEdgePosition(node: Node, intersectionPoint: XYPosition) {
  const n = { ...node.position, ...node }
  const nx = Math.round(n.x)
  const ny = Math.round(n.y)
  const px = Math.round(intersectionPoint.x)
  const py = Math.round(intersectionPoint.y)

  if (px <= nx + 1) {
    return Position.Left
  }
  if (px >= nx + (n.measured?.width ?? 0) - 1) {
    return Position.Right
  }
  if (py <= ny + 1) {
    return Position.Top
  }
  if (py >= n.y + (n.measured?.height ?? 0) - 1) {
    return Position.Bottom
  }

  return Position.Top
}

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
export function getEdgeParams(source: InternalNode, target: InternalNode) {
  // no padding on source (no arrow), padding on target (arrow head)
  const sourceIntersectionPoint = getNodeIntersection(source, target, 0)
  const targetIntersectionPoint = getNodeIntersection(target, source, 0)

  const sourcePos = getEdgePosition(source, sourceIntersectionPoint)
  const targetPos = getEdgePosition(target, targetIntersectionPoint)

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos,
  }
}
